import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map, of } from 'rxjs';

import { environment } from '../../environments/environment';
import {
  MOCK_ACCESS_LOG, MOCK_MESSAGES, MOCK_PARTNER_ORG_GRANTS, MOCK_PARTNERS, MOCK_QUEUES,
  MOCK_QUEUE_GRANTEES, MOCK_TICKETS, MOCK_TRANSITIONS, MOCK_VAR_PRINCIPALS, mockTicketAccess,
} from './mock-data';
import {
  CertificationOverview, CertificationRosterEntry, CertificationStatus,
  CreatePartnerPayload, CreateTicketPayload, CreateVarPrincipalPayload,
  ExamAttemptSummary, IntakePayload,
  AlertSettings, PartnerOrgGrant, PartnerSummary, UpdatePartnerPayload, PatchTicketPayload, QueueGrantee, QueueSummary,
  SectionModule, SectionQuizResult, SectionQuizStart,
  TicketAccessLogEntry, TicketAccessResult, TicketEventsPage, TicketFull, TicketListFilters, TicketMessageDetail,
  TicketSafe, TicketSummary, TicketTransition, VarPrincipalSummary, Visibility,
} from './models';
import { isResolvable } from './ticket-status';

/**
 * Thin HTTP client for the support-ticketing API surface consumed by the
 * agent console. Field names on request/response bodies are deliberately
 * left in the API's own snake_case — never re-cased — so the wire shape is
 * traceable straight back to the backend dataclasses.
 */
@Injectable({ providedIn: 'root' })
export class SupportApiService {
  private http = inject(HttpClient);
  private base = `${environment.apiBaseUrl}/api/v1`;

  listQueues(): Observable<QueueSummary[]> {
    if (environment.useMockData && !environment.production) return of(MOCK_QUEUES);
    return this.http.get<QueueSummary[]>(`${this.base}/queues`);
  }

  /** Who can be handed work on this queue (AB#1883) — the assign picker's roster.
   *  BT-staff only server-side; a safe-projection (VAR) reader never renders the
   *  control that consumes this, so a 403 here is expected rather than exceptional. */
  listQueueGrantees(queueId: string): Observable<QueueGrantee[]> {
    if (environment.useMockData && !environment.production) return of(MOCK_QUEUE_GRANTEES);
    return this.http.get<QueueGrantee[]>(`${this.base}/queues/${encodeURIComponent(queueId)}/grantees`);
  }

  listTickets(filters: TicketListFilters): Observable<TicketSummary[]> {
    if (environment.useMockData && !environment.production) {
      const rows = MOCK_TICKETS.filter((row) =>
        (!filters.status || row.status === filters.status
          || (filters.status === 'open' && isResolvable(row.status))) &&
        (!filters.priority || row.priority === filters.priority) &&
        (!filters.q || row.subject.toLowerCase().includes(filters.q.toLowerCase())));
      return of(rows);
    }
    let params = new HttpParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    }
    return this.http.get<TicketSummary[]>(`${this.base}/tickets`, { params });
  }

  /**
   * Fetches a single ticket and tags the response by projection: a "full"
   * agent-view ticket carries a `version` field (optimistic-locking column);
   * a "safe" VAR-projection ticket does not. Callers must branch on
   * `projection`, never assume shape.
   */
  getTicket(id: string): Observable<TicketAccessResult> {
    if (environment.useMockData && !environment.production) return of(mockTicketAccess(id));
    return this.http.get<TicketFull | TicketSafe>(`${this.base}/tickets/${id}`).pipe(
      map((ticket) => ({
        projection: 'version' in ticket ? ('full' as const) : ('safe' as const),
        ticket,
      })),
    );
  }

  getTicketMessages(id: string): Observable<TicketMessageDetail[]> {
    if (environment.useMockData && !environment.production) return of(MOCK_MESSAGES);
    return this.http.get<TicketMessageDetail[]>(`${this.base}/tickets/${id}/messages`);
  }

  /**
   * Catch-up events since `since` — the ticket thread's CHANGE DETECTOR, deliberately
   * NOT `getTicketMessages`.
   *
   * `GET /tickets/{id}/messages` writes a `support.ticket_access_log` row on every
   * non-requester read, so polling it on a 3s cadence would manufacture ~1200 CJI
   * read-audit rows per open-tab hour and drown the real operator reads that table
   * exists to record (compliance-scan AU-2/AU-6/AU-12, PR #1571). This endpoint writes
   * no audit row, so the poll asks the cheap question here and only reads messages when
   * something actually changed.
   */
  getTicketEvents(id: string, since: number): Observable<TicketEventsPage> {
    if (environment.useMockData && !environment.production) return of({ events: [], currentEventSeq: since });
    return this.http.get<TicketEventsPage>(`${this.base}/tickets/${id}/events`, {
      params: new HttpParams().set('since', String(since)),
    });
  }

  getTicketTransitions(id: string): Observable<TicketTransition[]> {
    if (environment.useMockData && !environment.production) return of(MOCK_TRANSITIONS);
    return this.http.get<TicketTransition[]>(`${this.base}/tickets/${id}/transitions`);
  }

  getTicketAccessLog(id: string): Observable<TicketAccessLogEntry[]> {
    if (environment.useMockData && !environment.production) return of(MOCK_ACCESS_LOG);
    return this.http.get<TicketAccessLogEntry[]>(`${this.base}/tickets/${id}/access-log`);
  }

  patchTicket(id: string, payload: PatchTicketPayload): Observable<{ status: string }> {
    return this.http.patch<{ status: string }>(`${this.base}/tickets/${id}`, payload);
  }

  postMessage(id: string, body: string, visibility: Visibility): Observable<{ id: string; ordinal: number }> {
    return this.http.post<{ id: string; ordinal: number }>(`${this.base}/tickets/${id}/messages`, { body, visibility });
  }

  ackPage(id: string): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${this.base}/tickets/${id}/page/ack`, {});
  }

  createTicket(payload: CreateTicketPayload): Observable<{ id: string; reference: string; status: string; priority: string }> {
    return this.http.post<{ id: string; reference: string; status: string; priority: string }>(`${this.base}/tickets`, payload);
  }

  /**
   * Unauthenticated "I can't log in" intake. Posts to the PUBLIC POST /intake
   * endpoint — no bearer token (the caller has no MSAL account, so the auth
   * interceptor attaches none), no client-chosen queue (the server resolves the
   * triage queue and forces high priority). The honeypot field travels as
   * `website` and must be empty for a real submission.
   */
  submitIntake(payload: IntakePayload): Observable<{ reference: string; status: string }> {
    return this.http.post<{ reference: string; status: string }>(`${this.base}/intake`, payload);
  }

  listVarPrincipals(): Observable<VarPrincipalSummary[]> {
    if (environment.useMockData && !environment.production) return of(MOCK_VAR_PRINCIPALS);
    return this.http.get<VarPrincipalSummary[]>(`${this.base}/admin/var-principals`);
  }

  /** Create/disable are real writes even in mock-data mode — there is no
   * consequence-free mock to fake a provisioning decision against, so both
   * always hit the live endpoint (same posture as createTicket/patchTicket). */
  createVarPrincipal(payload: CreateVarPrincipalPayload): Observable<VarPrincipalSummary> {
    return this.http.post<VarPrincipalSummary>(`${this.base}/admin/var-principals`, payload);
  }

  disableVarPrincipal(id: string): Observable<VarPrincipalSummary> {
    return this.http.post<VarPrincipalSummary>(`${this.base}/admin/var-principals/${id}/disable`, {});
  }

  // --- Partners + per-partner org entitlement (migration 010) ---------------

  listPartners(): Observable<PartnerSummary[]> {
    if (environment.useMockData && !environment.production) return of(MOCK_PARTNERS);
    return this.http.get<PartnerSummary[]>(`${this.base}/admin/partners`);
  }

  /** Create is a real write even in mock-data mode (same posture as
   * createVarPrincipal — no consequence-free mock of a provisioning decision). */
  updatePartner(partnerOrgKey: string, payload: UpdatePartnerPayload): Observable<PartnerSummary> {
    return this.http.patch<PartnerSummary>(
      `${this.base}/admin/partners/${encodeURIComponent(partnerOrgKey)}`, payload);
  }

  getAlertSettings(): Observable<AlertSettings> {
    if (environment.useMockData && !environment.production) {
      return of({ default_alert_email: 'support@bluetitan.com', version: 1, updated_at: new Date().toISOString() });
    }
    return this.http.get<AlertSettings>(`${this.base}/admin/alert-settings`);
  }

  updateAlertSettings(defaultAlertEmail: string, version: number): Observable<AlertSettings> {
    return this.http.patch<AlertSettings>(`${this.base}/admin/alert-settings`,
      { default_alert_email: defaultAlertEmail, version });
  }

  createPartner(payload: CreatePartnerPayload): Observable<PartnerSummary> {
    return this.http.post<PartnerSummary>(`${this.base}/admin/partners`, payload);
  }

  listPartnerOrgGrants(partnerOrgKey: string): Observable<PartnerOrgGrant[]> {
    if (environment.useMockData && !environment.production) {
      return of(MOCK_PARTNER_ORG_GRANTS[partnerOrgKey] ?? []);
    }
    return this.http.get<PartnerOrgGrant[]>(
      `${this.base}/admin/partners/${encodeURIComponent(partnerOrgKey)}/org-grants`,
    );
  }

  grantPartnerOrg(partnerOrgKey: string, organizationId: string): Observable<PartnerOrgGrant> {
    return this.http.post<PartnerOrgGrant>(
      `${this.base}/admin/partners/${encodeURIComponent(partnerOrgKey)}/org-grants`,
      { organization_id: organizationId },
    );
  }

  revokePartnerOrgGrant(grantId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/admin/partner-org-grants/${grantId}`);
  }

  // --- VAR-agent certification gate (Task 4 endpoints) -----------------------

  listCertifications(): Observable<CertificationRosterEntry[]> {
    return this.http.get<CertificationRosterEntry[]>(`${this.base}/admin/certifications`);
  }

  getCertification(principalId: string): Observable<CertificationStatus> {
    return this.http.get<CertificationStatus>(`${this.base}/admin/principals/${principalId}/certification`);
  }

  /** Grant/renew and revoke are real writes even in mock mode, matching
   * createVarPrincipal's stated posture — there is no consequence-free mock
   * of a certification decision. */
  grantCertification(principalId: string): Observable<CertificationStatus> {
    return this.http.post<CertificationStatus>(
      `${this.base}/admin/principals/${principalId}/certification`, { granted_via: 'admin_attestation' });
  }

  revokeCertification(principalId: string, reason: string): Observable<CertificationStatus> {
    return this.http.post<CertificationStatus>(
      `${this.base}/admin/principals/${principalId}/certification/revoke`, { reason });
  }

  // --- VAR-agent certification exam — admin attempt history + reset ---------

  getExamAttempts(principalId: string): Observable<ExamAttemptSummary[]> {
    return this.http.get<ExamAttemptSummary[]>(`${this.base}/admin/principals/${principalId}/exam-attempts`);
  }

  resetExamAttempts(principalId: string): Observable<void> {
    return this.http.post<void>(`${this.base}/admin/principals/${principalId}/exam/reset`, {});
  }

  // --- VAR-agent certification — per-section quiz flow (Task 5 endpoints,
  // AB#1827 follow-on). Every one of these hits `/me/certification/*`, which
  // the server scopes to the signed-in principal via `require_var_self` —
  // never an admin route, and never a principal id supplied by this client.
  // Replaces the old single-exam taker methods (removed in Task 9). --------

  /** Overall eligibility plus per-section pass state across all 6 sections. */
  getCertificationOverview(): Observable<CertificationOverview> {
    return this.http.get<CertificationOverview>(`${this.base}/me/certification`);
  }

  /** One section's study content. 404s (propagated to the caller) for an
   * unknown section code. */
  getSectionModule(section: string): Observable<SectionModule> {
    return this.http.get<SectionModule>(
      `${this.base}/me/certification/modules/${encodeURIComponent(section)}`,
    );
  }

  /** Starts a frozen 5-item quiz for one section. 404 unknown_module, 503
   * pool_too_small — both propagated to the caller. */
  startSectionQuiz(section: string): Observable<SectionQuizStart> {
    return this.http.post<SectionQuizStart>(
      `${this.base}/me/certification/modules/${encodeURIComponent(section)}/quiz/start`, {},
    );
  }

  /** Grades one section's quiz attempt and returns the taker's updated
   * overall standing. */
  submitSectionQuiz(section: string, attemptId: string, answers: Record<string, string[]>): Observable<SectionQuizResult> {
    return this.http.post<SectionQuizResult>(
      `${this.base}/me/certification/modules/${encodeURIComponent(section)}/quiz/${encodeURIComponent(attemptId)}/submit`,
      { answers },
    );
  }
}
